import { Button } from '@thanx/uikit/button'
import ModalContainer from 'components/ModalContainer'
import { buildTranslate } from 'locales'
import React from 'react'

const t = buildTranslate('recovery_ai.config_modal')

type Props = {
  isOpen: boolean
  /** Modal title. Omit to render a title-less sub-view (e.g. the brand-voice
   *  "← Back" upload view). When omitted, pass `ariaLabel` — otherwise the
   *  dialog falls back to Base Web's literal "dialog". */
  title?: string
  /** Explicit accessible name; defaults to `title` via ModalContainer. */
  ariaLabel?: string
  children: React.ReactNode
  /** Wired to the default footer's Save. Optional: a consumer that supplies its
   *  own `footer` (e.g. brand voice) drives its buttons directly and omits this. */
  onSave?: () => void
  onSaveAndTest?: () => void
  onCancel: () => void
  /** When false, Save is disabled — e.g. the caller lacks manage permission or
   *  the config hasn't loaded (or failed to). Deliberately NOT gated on whether
   *  anything is staged: clicking Save with nothing staged is a safe no-op (the
   *  caller's commit() resolves true without a PUT) and should close the modal
   *  rather than sit disabled. */
  canSave?: boolean
  /** When false, Save-and-test is disabled — used to gate it while the config
   *  GET is loading/errored, so it never looks clickable while its handler
   *  silently no-ops (Save is separately gated by `canSave`). */
  canSaveAndTest?: boolean
  /** True while the commit PUT is in flight — disables Save / Save-and-test and
   *  shows the Save spinner so a double-click can't fire a duplicate write. */
  isSaving?: boolean
  /** Non-save work in flight (brand-voice derive, make-good regenerate) that must
   *  also block dismiss and Save-and-test. Kept separate from isSaving so Save's
   *  spinner still means "saving" and nothing else. Deliberately does NOT gate
   *  plain Save: the config PUT and a make-good regenerate touch disjoint cache
   *  tags (RecoveryConfig vs RecoveryMakeGoodLadder — CONTRACT.md §5.6), so
   *  committing a staged config edit while a regenerate is in flight is safe,
   *  and blocking it would just make the merchant wait on an unrelated write. */
  isBusy?: boolean
  /** Dialog width, forwarded to ModalContainer. OMIT to keep ModalContainer's
   *  600px default — omission IS how the shipped modals keep their appearance,
   *  so this deliberately has no default here. */
  width?: string
  /** Pass-through anchor id for a future guided-tour target (F7). */
  id?: string
  /** Replaces the default Cancel / Save-and-test / Save footer. Brand voice's
   *  edit view uses a two-button (Cancel / Save changes) footer and its upload
   *  sub-view a single "Generate"; every other consumer keeps the default. */
  footer?: React.ReactNode
  /** Forwarded to ModalContainer to render the "← Back" sub-view header. */
  onBack?: VoidFunction
  /** Overrides the default footer's Save-button copy (defaults to "Save"). The
   *  Approved-rewards row passes "Save changes"; the other rows keep the default. */
  saveLabel?: string
}

// R12 (AI-168) F1 shell: a controlled modal that dims the simulator behind
// ModalContainer's scrim and hosts an arbitrary config-pane body. By default it
// renders a three-action footer (Cancel / Save / Save-and-test); a consumer can
// override the footer (and add a "← Back" header) via the `footer` / `onBack`
// props without disturbing the other three modals. Presentational and controlled
// — it does NOT own the staged-save draft; the caller wires Save → commit() and
// Cancel → rollback(). The X / scrim-dismiss both route to onCancel so dismissing
// the modal can never silently persist a staged edit.
const ConfigModalHost = ({
  isOpen,
  title,
  ariaLabel,
  children,
  onSave,
  onSaveAndTest,
  onCancel,
  canSave = true,
  canSaveAndTest = true,
  isSaving = false,
  isBusy = false,
  width,
  id,
  footer,
  onBack,
  saveLabel,
}: Props) => {
  // PR#1: block every dismiss path (Cancel button below + ModalContainer's X /
  // scrim-click / Escape, which all route through onClose) while a save is in
  // flight. Cancelling mid-PUT calls the caller's rollback(), which would wipe
  // the staged edit out from under the in-flight write and tear down the modal
  // before its success/error resolves. A no-op guard here is the catch-all;
  // Save already shows a spinner so the in-flight state is visible. isBusy
  // extends the same block to non-save writes (derive / regenerate), which are
  // just as unsafe to tear down mid-flight.
  const handleClose = () => {
    if (isSaving || isBusy) return
    onCancel()
  }

  // Default Cancel / Save-and-test / Save footer, used unless `footer` overrides.
  // hideCancel is set below so this is the only Cancel (full control over copy
  // + order). Save-and-test commits then routes to the simulator (see
  // useSaveAndTest); all three actions are blocked while a save is in flight.
  // Save-and-test renders only when a handler is supplied: surfaces where the
  // simulator is already on-screen (the wizard's Testing stage) omit it, and a
  // button wired to nothing would otherwise sit there enabled and inert.
  const defaultFooter = (
    <>
      <Button
        kind="secondary"
        onClick={handleClose}
        disabled={isSaving || isBusy}
      >
        {t('cancel')}
      </Button>
      {onSaveAndTest && (
        <Button
          kind="secondary"
          className="ml-s"
          onClick={onSaveAndTest}
          disabled={!canSaveAndTest || isSaving || isBusy}
        >
          {t('save_and_test')}
        </Button>
      )}
      <Button
        kind="primary"
        className="ml-s"
        onClick={onSave}
        // isBusy is deliberately excluded here (see the prop doc above): Save's
        // config PUT and a make-good regenerate touch disjoint cache tags, so a
        // staged edit is safe to commit while a regenerate is in flight.
        disabled={!canSave || isSaving}
        isLoading={isSaving}
      >
        {saveLabel ?? t('save')}
      </Button>
    </>
  )

  return (
    <ModalContainer
      isOpen={isOpen}
      title={title}
      ariaLabel={ariaLabel}
      onClose={handleClose}
      onBack={onBack}
      width={width}
      hideCancel
      showBottomProceed={false}
      buttonGroupChildren={footer ?? defaultFooter}
      // ModalContainer defaults to zIndex 3, below the fixed header's
      // z-index: 900 (src/index.scss). Tall bodies (e.g. the approved-rewards
      // grid) push the dialog's top — title + close X — behind the header,
      // making it uncloseable. 1000 matches the established
      // ModalContainer/Modal override used app-wide for this same reason
      // (e.g. SmartLinkViewModal, PreviewModal, AISegmentBuilder).
      zIndex={1000}
    >
      <div id={id}>{children}</div>
    </ModalContainer>
  )
}

export default ConfigModalHost
